import React, { useState, useEffect, useCallback } from 'react';
import { FileText, ChevronDown, ChevronRight, ChevronLeft, Dumbbell, CalendarDays, Activity, List, Plus, Trash2, X, Edit } from 'lucide-react';
import { trainerAddPtLog, trainerUpdatePtLog, trainerDeletePtLog } from '../../lib/trainerDb';
import { trainerAddBodyRecord, getMemberBodyRecords, deleteBodyRecord } from '../../lib/bodyRecords';
import { schedulePtSession, completePtSession, cancelPtSession } from '../../lib/ptSessions';
import { format, parseISO, isAfter } from 'date-fns';
import { ko } from 'date-fns/locale';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// PT 수업 기록 폼
function PtLogForm({ assignment, member, onClose, onLogged, editLog }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    date: editLog?.date || today,
    content: editLog?.content || '',
    memo: editLog?.memo || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const remaining = assignment.totalSessions - assignment.usedSessions;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.content.trim()) { setError('운동 내용을 입력해주세요.'); return; }
    setLoading(true);
    if (editLog) {
      const { error: err } = await trainerUpdatePtLog(editLog.id, { content: form.content, memo: form.memo });
      setLoading(false);
      if (err) { setError('수정 중 오류가 발생했습니다.'); return; }
    } else {
      const { error: err } = await trainerAddPtLog({
        assignmentId: assignment.id,
        date: form.date,
        content: form.content,
        memo: form.memo,
      });
      setLoading(false);
      if (err) { setError('기록 중 오류가 발생했습니다.'); return; }
    }
    onLogged();
    onClose();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
        <span className="font-semibold">{member?.name}</span> 회원 ·{' '}
        {assignment.productName} · 잔여 <span className="font-bold">{remaining}회</span>
      </div>
      <div>
        <label className="label">날짜</label>
        <input
          type="date"
          className="input"
          value={form.date}
          onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
          required
        />
      </div>
      <div>
        <label className="label">운동 내용 <span className="text-red-500">*</span></label>
        <textarea
          className="input min-h-[100px] resize-none"
          rows={4}
          placeholder="스쿼트 3세트 15회, 데드리프트 3세트 8회..."
          value={form.content}
          onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
          required
        />
      </div>
      <div>
        <label className="label">메모 (선택)</label>
        <textarea
          className="input resize-none"
          rows={2}
          placeholder="특이사항, 다음 목표, 컨디션 등..."
          value={form.memo}
          onChange={e => setForm(p => ({ ...p, memo: e.target.value }))}
        />
      </div>
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary" disabled={loading || (!editLog && remaining <= 0)}>
          {loading ? '저장 중...' : editLog ? '수정' : '수업 완료'}
        </button>
      </div>
    </form>
  );
}

// 신체 정보 기록 폼
function BodyRecordForm({ member, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ date: today, weight: '', bodyFat: '', muscleMass: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.weight && !form.bodyFat && !form.muscleMass) {
      setError('최소 하나의 측정값을 입력해주세요.');
      return;
    }
    setLoading(true);
    const { error: err } = await trainerAddBodyRecord({
      memberId: member.id,
      date: form.date,
      weight: form.weight ? Number(form.weight) : null,
      bodyFat: form.bodyFat ? Number(form.bodyFat) : null,
      muscleMass: form.muscleMass ? Number(form.muscleMass) : null,
      notes: form.notes,
    });
    setLoading(false);
    if (err) { setError('저장 중 오류가 발생했습니다.'); return; }
    onSaved?.();
    onClose();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
        <span className="font-semibold">{member?.name}</span> 회원 신체 정보 기록
      </div>
      <div>
        <label className="label">날짜</label>
        <input type="date" className="input" value={form.date} max={today} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">체중 (kg)</label>
          <input type="number" step="0.1" className="input" value={form.weight} onChange={e => setForm(p => ({ ...p, weight: e.target.value }))} placeholder="70.5" />
        </div>
        <div>
          <label className="label">체지방 (%)</label>
          <input type="number" step="0.1" className="input" value={form.bodyFat} onChange={e => setForm(p => ({ ...p, bodyFat: e.target.value }))} placeholder="18.5" />
        </div>
        <div>
          <label className="label">근육량 (kg)</label>
          <input type="number" step="0.1" className="input" value={form.muscleMass} onChange={e => setForm(p => ({ ...p, muscleMass: e.target.value }))} placeholder="55.0" />
        </div>
      </div>
      <div>
        <label className="label">메모 (선택)</label>
        <input className="input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="특이사항 등..." />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary" disabled={loading}>{loading ? '저장 중...' : '저장'}</button>
      </div>
    </form>
  );
}

// 주간 캘린더
function WeeklyCalendar({ sessions, onSelectSession }) {
  const [weekOffset, setWeekOffset] = useState(0);

  const todayBase = new Date();
  todayBase.setHours(0, 0, 0, 0);

  const dayOfWeek = todayBase.getDay();
  const monday = new Date(todayBase);
  monday.setDate(todayBase.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + weekOffset * 7);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const todayStr = format(todayBase, 'yyyy-MM-dd');

  const sessionsByDate = {};
  sessions.forEach(s => {
    const key = format(parseISO(s.scheduledAt), 'yyyy-MM-dd');
    if (!sessionsByDate[key]) sessionsByDate[key] = [];
    sessionsByDate[key].push(s);
  });

  const DAY_KO = ['월', '화', '수', '목', '금', '토', '일'];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setWeekOffset(w => w - 1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {format(weekDays[0], 'M.d')} – {format(weekDays[6], 'M.d')}
          </span>
          {weekOffset !== 0 && (
            <button
              onClick={() => setWeekOffset(0)}
              className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            >
              오늘
            </button>
          )}
        </div>
        <button
          onClick={() => setWeekOffset(w => w + 1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[480px]">
          {weekDays.map((day, i) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const daySessions = [...(sessionsByDate[dateKey] || [])].sort((a, b) =>
              a.scheduledAt.localeCompare(b.scheduledAt)
            );
            const isToday = dateKey === todayStr;
            const isPast = day < todayBase;
            const isSun = i === 6;
            const isSat = i === 5;

            return (
              <div
                key={dateKey}
                className={`rounded-xl border flex flex-col ${
                  isToday
                    ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className={`px-1 py-1.5 text-center border-b ${
                  isToday ? 'border-blue-200 dark:border-blue-700' : 'border-gray-100 dark:border-gray-700'
                }`}>
                  <p className={`text-[10px] font-medium ${
                    isSun ? 'text-red-400' : isSat ? 'text-blue-400' : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {DAY_KO[i]}
                  </p>
                  <p className={`text-sm font-bold leading-tight ${
                    isToday ? 'text-blue-600 dark:text-blue-400' :
                    isPast ? 'text-gray-400' : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {format(day, 'd')}
                  </p>
                </div>
                <div className="p-1 space-y-0.5 min-h-[80px]">
                  {daySessions.map(s => (
                    <button
                      key={s.id}
                      onClick={() => s.status === 'scheduled' && onSelectSession(s)}
                      className={`w-full text-left rounded-lg px-1.5 py-1 text-[11px] transition-opacity ${
                        s.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 cursor-default'
                          : s.status === 'cancelled'
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-default'
                          : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:opacity-75'
                      }`}
                    >
                      <p className="font-semibold truncate">{format(parseISO(s.scheduledAt), 'HH:mm')} {s.memberName}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

// 시작일 기준으로 선택된 요일 × 주수에 해당하는 날짜 목록 생성
function generateDates(startDate, selectedDays, weeks) {
  const dates = [];
  const base = new Date(startDate + 'T00:00:00');
  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < 7; d++) {
      const candidate = new Date(base);
      candidate.setDate(base.getDate() + w * 7 + d);
      if (selectedDays.includes(candidate.getDay())) {
        const iso = candidate.toISOString().split('T')[0];
        if (iso >= startDate) dates.push(iso);
      }
    }
  }
  return dates.sort();
}

// 트레이너 일정 등록 폼
function TrainerScheduleForm({ assignments, members, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [mode, setMode] = useState('single'); // 'single' | 'repeat'
  const [form, setForm] = useState({ assignmentId: '', date: today, time: '10:00', notes: '' });
  const [repeatDays, setRepeatDays] = useState([]); // 0=일,1=월,...
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  const activeAssignments = assignments.filter(a => a.status === 'active' && (a.totalSessions - a.usedSessions) > 0);
  const selectedAssignment = activeAssignments.find(a => a.id === form.assignmentId);
  const remaining = selectedAssignment ? selectedAssignment.totalSessions - selectedAssignment.usedSessions : 0;

  const repeatDates = mode === 'repeat' && form.date && repeatDays.length > 0
    ? generateDates(form.date, repeatDays, repeatWeeks).slice(0, remaining)
    : [];

  function toggleDay(d) {
    setRepeatDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.assignmentId) { setError('PT 배정을 선택해주세요.'); return; }
    setError('');

    if (mode === 'single') {
      setLoading(true);
      const scheduledAt = new Date(`${form.date}T${form.time}:00`).toISOString();
      const { error: err } = await schedulePtSession({ assignmentId: form.assignmentId, scheduledAt, notes: form.notes });
      setLoading(false);
      if (err) { setError('일정 등록 중 오류가 발생했습니다.'); return; }
      onSaved(); onClose();
    } else {
      if (repeatDays.length === 0) { setError('요일을 하나 이상 선택해주세요.'); return; }
      if (repeatDates.length === 0) { setError('등록할 날짜가 없습니다.'); return; }
      setLoading(true);
      let failed = 0;
      for (let i = 0; i < repeatDates.length; i++) {
        setProgress(`등록 중... (${i + 1}/${repeatDates.length})`);
        const scheduledAt = new Date(`${repeatDates[i]}T${form.time}:00`).toISOString();
        const { error: err } = await schedulePtSession({ assignmentId: form.assignmentId, scheduledAt, notes: form.notes });
        if (err) failed++;
      }
      setLoading(false);
      setProgress('');
      if (failed > 0) { setError(`${failed}건 등록 실패. 잔여 횟수를 초과했을 수 있습니다.`); return; }
      onSaved(); onClose();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 모드 토글 */}
      <div className="flex rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
        {[['single', '단일 등록'], ['repeat', '반복 등록']].map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setMode(val)}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              mode === val
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* PT 배정 선택 */}
      <div>
        <label className="label">PT 배정 선택</label>
        <select className="input" value={form.assignmentId} onChange={e => setForm(p => ({ ...p, assignmentId: e.target.value }))} required>
          <option value="">선택하세요</option>
          {activeAssignments.map(a => {
            const member = members.find(m => m.id === a.memberId);
            const rem = a.totalSessions - a.usedSessions;
            return (
              <option key={a.id} value={a.id}>
                {member?.name || '(회원)'} · {a.productName} · 잔여 {rem}회
              </option>
            );
          })}
        </select>
        {activeAssignments.length === 0 && (
          <p className="text-xs text-gray-400 mt-1">잔여 횟수가 있는 활성 PT가 없습니다.</p>
        )}
      </div>

      {/* 단일 모드: 날짜 + 시간 */}
      {mode === 'single' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">날짜</label>
            <input type="date" className="input" value={form.date} min={today} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
          </div>
          <div>
            <label className="label">시간</label>
            <input type="time" className="input" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} required />
          </div>
        </div>
      )}

      {/* 반복 모드: 시작일 + 요일 + 주수 */}
      {mode === 'repeat' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">시작일</label>
              <input type="date" className="input" value={form.date} min={today} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
            </div>
            <div>
              <label className="label">시간</label>
              <input type="time" className="input" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} required />
            </div>
          </div>
          <div>
            <label className="label">요일 선택</label>
            <div className="flex gap-2 flex-wrap">
              {DAY_LABELS.map((label, d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                    repeatDays.includes(d)
                      ? 'bg-primary-600 text-white'
                      : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">기간</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="input w-20"
                value={repeatWeeks}
                min={1}
                max={52}
                onChange={e => setRepeatWeeks(Number(e.target.value))}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">주</span>
              <div className="flex gap-1.5 ml-2">
                {[4, 8, 12].map(w => (
                  <button key={w} type="button" onClick={() => setRepeatWeeks(w)}
                    className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                    {w}주
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* 미리보기 */}
          {repeatDates.length > 0 && (
            <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl space-y-1.5">
              <p className="text-xs font-semibold text-primary-700 dark:text-primary-400">
                총 {repeatDates.length}회 등록
                {remaining > 0 && repeatDates.length === remaining && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">(잔여 횟수 한도)</span>
                )}
              </p>
              <div className="flex flex-wrap gap-1">
                {repeatDates.slice(0, 10).map(d => (
                  <span key={d} className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded text-gray-600 dark:text-gray-300">{d}</span>
                ))}
                {repeatDates.length > 10 && (
                  <span className="text-xs text-primary-500">+{repeatDates.length - 10}개 더</span>
                )}
              </div>
            </div>
          )}
          {repeatDays.length > 0 && form.assignmentId && repeatDates.length === 0 && (
            <p className="text-xs text-amber-500">선택한 요일에 해당하는 날짜가 없거나 잔여 횟수가 없습니다.</p>
          )}
        </>
      )}

      <div>
        <label className="label">메모 (선택)</label>
        <input className="input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="특이사항 등..." />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button
          type="submit"
          className="btn-primary"
          disabled={loading || activeAssignments.length === 0 || (mode === 'repeat' && repeatDates.length === 0)}
        >
          {loading ? (progress || '등록 중...') : (mode === 'repeat' ? `${repeatDates.length}회 일괄 등록` : '일정 등록')}
        </button>
      </div>
    </form>
  );
}

// PT 배정 카드
function PtCard({ assignment, member, logs, onLogged }) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' | 'body'
  const [logFormOpen, setLogFormOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [bodyFormOpen, setBodyFormOpen] = useState(false);
  const [deleteLogConfirm, setDeleteLogConfirm] = useState(null);
  const [deleteBodyConfirm, setDeleteBodyConfirm] = useState(null);
  const [bodyRecords, setBodyRecords] = useState([]);
  const [bodyLoaded, setBodyLoaded] = useState(false);

  const remaining = assignment.totalSessions - assignment.usedSessions;
  const progress = assignment.totalSessions > 0 ? (assignment.usedSessions / assignment.totalSessions) * 100 : 0;
  const cardLogs = logs.filter(l => l.ptAssignmentId === assignment.id)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const loadBodyRecords = useCallback(async () => {
    if (!member?.id) return;
    const records = await getMemberBodyRecords(member.id);
    setBodyRecords(records);
    setBodyLoaded(true);
  }, [member?.id]);

  useEffect(() => {
    if (expanded && !bodyLoaded) {
      loadBodyRecords();
    }
  }, [expanded, bodyLoaded, loadBodyRecords]);

  async function handleDeleteLog(log) {
    const { error } = await trainerDeletePtLog(log.id);
    if (!error) onLogged();
    setDeleteLogConfirm(null);
  }

  async function handleDeleteBody(record) {
    const { error } = await deleteBodyRecord(record.id);
    if (!error) {
      setBodyRecords(prev => prev.filter(r => r.id !== record.id));
    }
    setDeleteBodyConfirm(null);
  }

  function handleBodySaved() {
    loadBodyRecords();
  }

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white text-base">
            {member?.name || '(회원 정보 없음)'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{assignment.productName}</p>
        </div>
        <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${
          remaining <= 0
            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            : remaining <= 5
            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        }`}>
          잔여 {remaining}회
        </span>
      </div>

      {/* 진행바 */}
      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {assignment.usedSessions}/{assignment.totalSessions}회 완료
      </p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setExpanded(!expanded); setActiveTab('logs'); }}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            일지 {cardLogs.length}건
            {expanded && activeTab === 'logs' ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => { setExpanded(true); setActiveTab('body'); }}
            className={`flex items-center gap-1 text-xs hover:text-gray-700 dark:hover:text-gray-200 ${
              expanded && activeTab === 'body' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            신체 {bodyLoaded ? `${bodyRecords.length}건` : ''}
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setBodyFormOpen(true)}
            className="flex items-center gap-1 text-xs btn-secondary px-2.5 py-1.5"
            title="신체 정보 기록"
          >
            <Activity className="w-3.5 h-3.5" />
            신체
          </button>
          <button
            onClick={() => setLogFormOpen(true)}
            disabled={remaining <= 0}
            className="flex items-center gap-1.5 text-xs btn-primary px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileText className="w-3.5 h-3.5" />
            수업 기록
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {/* 탭 */}
          <div className="flex gap-3 mb-2">
            <button
              onClick={() => setActiveTab('logs')}
              className={`text-xs font-medium pb-1 border-b-2 transition-colors ${
                activeTab === 'logs' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-400'
              }`}
            >
              PT 일지 ({cardLogs.length})
            </button>
            <button
              onClick={() => setActiveTab('body')}
              className={`text-xs font-medium pb-1 border-b-2 transition-colors ${
                activeTab === 'body' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-400'
              }`}
            >
              신체 정보 ({bodyLoaded ? bodyRecords.length : '...'})
            </button>
          </div>

          {/* PT 일지 */}
          {activeTab === 'logs' && (
            <div className="space-y-2">
              {cardLogs.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">기록된 일지가 없습니다.</p>
              ) : (
                cardLogs.map(log => (
                  <div key={log.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 group">
                    <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                      <span>
                        {log.date ? format(parseISO(log.date), 'yyyy.MM.dd (EEE)', { locale: ko }) : ''}
                        {log.sessionNumber && <span className="ml-2">{log.sessionNumber}회차</span>}
                      </span>
                      <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                        <button
                          onClick={() => { setEditingLog(log); setLogFormOpen(true); }}
                          className="text-gray-400 hover:text-blue-600"
                          title="일지 수정"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => setDeleteLogConfirm(log)}
                          className="text-red-400 hover:text-red-600"
                          title="일지 삭제"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{log.content}</p>
                    {log.memo && <p className="text-xs text-gray-400 mt-1">📝 {log.memo}</p>}
                  </div>
                ))
              )}
            </div>
          )}

          {/* 신체 정보 */}
          {activeTab === 'body' && (
            <div className="space-y-2">
              {!bodyLoaded ? (
                <div className="flex justify-center py-4">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : bodyRecords.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">기록된 신체 정보가 없습니다.</p>
              ) : (
                bodyRecords.map(rec => (
                  <div key={rec.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 group">
                    <div className="flex justify-between items-start mb-1.5">
                      <span className="text-xs text-gray-400">
                        {rec.date ? format(parseISO(rec.date), 'yyyy.MM.dd (EEE)', { locale: ko }) : ''}
                      </span>
                      <button
                        onClick={() => setDeleteBodyConfirm(rec)}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all"
                        title="신체 정보 삭제"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      {rec.weight != null && (
                        <span className="text-gray-700 dark:text-gray-300">
                          체중 <span className="font-semibold">{rec.weight}kg</span>
                        </span>
                      )}
                      {rec.bodyFat != null && (
                        <span className="text-gray-700 dark:text-gray-300">
                          체지방 <span className="font-semibold">{rec.bodyFat}%</span>
                        </span>
                      )}
                      {rec.muscleMass != null && (
                        <span className="text-gray-700 dark:text-gray-300">
                          근육량 <span className="font-semibold">{rec.muscleMass}kg</span>
                        </span>
                      )}
                    </div>
                    {rec.notes && <p className="text-xs text-gray-400 mt-1">📝 {rec.notes}</p>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <Modal isOpen={logFormOpen} onClose={() => { setLogFormOpen(false); setEditingLog(null); }} title={editingLog ? 'PT 일지 수정' : 'PT 수업 기록'}>
        <PtLogForm
          assignment={assignment}
          member={member}
          editLog={editingLog}
          onClose={() => { setLogFormOpen(false); setEditingLog(null); }}
          onLogged={onLogged}
        />
      </Modal>

      <Modal isOpen={bodyFormOpen} onClose={() => setBodyFormOpen(false)} title="신체 정보 기록">
        <BodyRecordForm
          member={member}
          onClose={() => setBodyFormOpen(false)}
          onSaved={handleBodySaved}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteLogConfirm}
        title="PT 일지 삭제"
        message="이 PT 일지를 삭제하시겠습니까?"
        onConfirm={() => handleDeleteLog(deleteLogConfirm)}
        onCancel={() => setDeleteLogConfirm(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteBodyConfirm}
        title="신체 정보 삭제"
        message="이 신체 정보를 삭제하시겠습니까?"
        onConfirm={() => handleDeleteBody(deleteBodyConfirm)}
        onCancel={() => setDeleteBodyConfirm(null)}
      />
    </div>
  );
}

export default function TrainerPT({ assignments, completedAssignments = [], members, logs, sessions = [], onRefresh }) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [completeSession, setCompleteSession] = useState(null);
  const [completeContent, setCompleteContent] = useState('');
  const [completeMemo, setCompleteMemo] = useState('');
  const [completing, setCompleting] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [sessionError, setSessionError] = useState('');

  async function handleComplete() {
    if (!completeSession) return;
    setCompleting(true);
    setSessionError('');
    const { error } = await completePtSession(completeSession.id, completeContent, completeMemo);
    setCompleting(false);
    if (error) {
      setSessionError('완료 처리 중 오류가 발생했습니다. 다시 시도해 주세요.');
      return;
    }
    setCompleteSession(null);
    setCompleteContent('');
    setCompleteMemo('');
    onRefresh();
  }

  async function handleCancel() {
    if (!cancelConfirm) return;
    setSessionError('');
    const { error } = await cancelPtSession(cancelConfirm.id);
    if (error) {
      setSessionError('취소 처리 중 오류가 발생했습니다. 다시 시도해 주세요.');
      setCancelConfirm(null);
      return;
    }
    setCancelConfirm(null);
    onRefresh();
  }

  const upcomingSessions = sessions.filter(
    s => s.status === 'scheduled' && isAfter(parseISO(s.scheduledAt), new Date())
  );

  if (assignments.length === 0 && completedAssignments.length === 0 && upcomingSessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
          <Dumbbell className="w-8 h-8 text-blue-400" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">배정된 PT가 없습니다</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">헬스장 관리자에게 PT를 배정받으세요.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {sessionError && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300 flex items-center justify-between">
          <span>{sessionError}</span>
          <button onClick={() => setSessionError('')} className="text-red-400 hover:text-red-600 ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* 예정 일정 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-blue-500" />
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              예정 일정 {upcomingSessions.length > 0 && <span className="text-blue-500">({upcomingSessions.length})</span>}
            </p>
          </div>
          {sessions.length > 0 && (
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <List className="w-3 h-3" />
                목록
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <CalendarDays className="w-3 h-3" />
                캘린더
              </button>
            </div>
          )}
        </div>

        {viewMode === 'calendar' && sessions.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
            <WeeklyCalendar
              sessions={sessions}
              onSelectSession={(s) => { setCompleteSession(s); setCompleteContent(''); setCompleteMemo(''); }}
            />
          </div>
        ) : upcomingSessions.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">예정된 PT 일정이 없습니다.</p>
        ) : (
          <div className="space-y-2">
            {upcomingSessions.map(s => (
              <div key={s.id} className="bg-blue-50 dark:bg-blue-900/20 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{s.memberName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{s.productName}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {format(parseISO(s.scheduledAt), 'M/d (EEE)', { locale: ko })}
                  </p>
                  <p className="text-xs text-gray-500">{format(parseISO(s.scheduledAt), 'HH:mm')}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => { setCompleteSession(s); setCompleteContent(''); setCompleteMemo(''); }}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors font-medium"
                  >
                    완료
                  </button>
                  <button
                    onClick={() => setCancelConfirm(s)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="일정 취소"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900 dark:text-white">
          담당 PT <span className="text-blue-600 dark:text-blue-400">{assignments.length}건</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScheduleOpen(true)}
            className="flex items-center gap-1 text-xs btn-primary px-3 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            일정 등록
          </button>
          <button
            onClick={onRefresh}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            새로고침
          </button>
        </div>
      </div>

      <Modal isOpen={scheduleOpen} onClose={() => setScheduleOpen(false)} title="PT 일정 등록">
        <TrainerScheduleForm
          assignments={assignments}
          members={members}
          onClose={() => setScheduleOpen(false)}
          onSaved={onRefresh}
        />
      </Modal>

      <Modal isOpen={!!completeSession} onClose={() => { setCompleteSession(null); setCompleteContent(''); setCompleteMemo(''); }} title="PT 수업 완료">
        {completeSession && (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-green-700 dark:text-green-300">
              <span className="font-semibold">{completeSession.memberName}</span> ·{' '}
              {format(parseISO(completeSession.scheduledAt), 'M/d (EEE) HH:mm', { locale: ko })}
            </div>
            <div>
              <label className="label">운동 내용 <span className="text-gray-400 font-normal">(선택)</span></label>
              <textarea
                className="input resize-none"
                rows={3}
                placeholder="스쿼트 3세트, 데드리프트 3세트... (미입력 시 'PT 수업 완료'로 자동 기록)"
                value={completeContent}
                onChange={e => setCompleteContent(e.target.value)}
              />
            </div>
            <div>
              <label className="label">
                회원 코멘트 <span className="text-gray-400 font-normal">(선택 · 회원 앱에 표시됩니다)</span>
              </label>
              <textarea
                className="input resize-none"
                rows={2}
                placeholder="오늘 정말 열심히 하셨어요! 스쿼트 자세가 많이 좋아졌습니다 💪"
                value={completeMemo}
                onChange={e => setCompleteMemo(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => { setCompleteSession(null); setCompleteContent(''); setCompleteMemo(''); }} className="btn-secondary">취소</button>
              <button
                onClick={handleComplete}
                disabled={completing}
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {completing ? '처리 중...' : '완료 처리'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!cancelConfirm}
        title="일정 취소"
        message={`${cancelConfirm?.memberName} 회원의 ${cancelConfirm ? format(parseISO(cancelConfirm.scheduledAt), 'M/d HH:mm') : ''} 일정을 취소하시겠습니까?`}
        onConfirm={handleCancel}
        onCancel={() => setCancelConfirm(null)}
      />

      {assignments.map(a => {
        const member = members.find(m => m.id === a.memberId);
        return (
          <PtCard
            key={a.id}
            assignment={a}
            member={member}
            logs={logs}
            onLogged={onRefresh}
          />
        );
      })}

      {completedAssignments.length > 0 && (
        <>
          <h2 className="font-semibold text-gray-500 dark:text-gray-400 mt-6">
            완료된 PT <span className="text-gray-400">{completedAssignments.length}건</span>
          </h2>
          {completedAssignments.map(a => {
            const member = members.find(m => m.id === a.memberId);
            return (
              <PtCard
                key={a.id}
                assignment={a}
                member={member}
                logs={logs}
                onLogged={onRefresh}
              />
            );
          })}
        </>
      )}
    </div>
  );
}
